import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BookOpen, Plus, Pencil, Trash2, Search } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const MEALS = ["diner", "souper", "gouter"];
const COURSES = ["entree", "plat", "dessert"];

export default function MenuPlats() {
  const { t } = useTranslation();
  const [dishes, setDishes] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [meal, setMeal] = useState("diner");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/cuisine/dishes"); setDishes(data.dishes || []); setCanEdit(!!data.can_edit); }
    catch { }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("dish.nameRequired"));
    try {
      if (form.id) await api.put(`/cuisine/dishes/${form.id}`, form);
      else await api.post("/cuisine/dishes", form);
      toast.success(t("dish.saved")); setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const remove = async (d) => {
    if (!window.confirm(t("dish.confirmDelete", { name: d.name }))) return;
    try { await api.delete(`/cuisine/dishes/${d.id}`); toast.success(t("dish.deleted")); load(); } catch { }
  };
  const toggleAllerg = (n) => {
    const cur = (form.allergens || "").split("/").filter(Boolean);
    const s = String(n);
    const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
    setForm({ ...form, allergens: next.map(Number).sort((a, b) => a - b).join("/") });
  };
  const list = (course) => dishes.filter((d) => d.meal === meal && d.course === course && (!q || d.name.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-5 p-4 sm:p-6" data-testid="menu-plats">
      <div className="rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #500724 0%, #9d174d 55%, #db2777 100%)" }}>
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-white/60 flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> {t("cuisine.title")}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">{t("nav.menuPlats")}</h1>
        <p className="text-white/70 text-sm mt-1">{t("dish.subtitle")}</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {MEALS.map((m) => (
          <button key={m} data-testid={`dish-meal-${m}`} onClick={() => setMeal(m)}
            className={`px-4 h-9 rounded-full text-sm font-semibold border transition ${meal === m ? "bg-pink-700 text-white border-pink-700" : "hover:bg-white/10"}`}>{t(`cuisine.${m}`)}</button>
        ))}
        <div className="relative ml-auto"><Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="dish-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("inv.search")} className="pl-8 h-9 w-52" /></div>
        {canEdit && <Button size="sm" className="bg-pink-700 hover:bg-pink-800 text-white rounded-full" data-testid="dish-add" onClick={() => setForm({ id: null, name: "", meal, course: "plat", allergens: "" })}><Plus className="h-4 w-4 mr-1" /> {t("dish.add")}</Button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COURSES.map((c) => (
          <div key={c} className="rounded-2xl glass p-4" data-testid={`dish-col-${c}`}>
            <h3 className="font-display font-bold text-pink-400 uppercase text-sm mb-3">{t(`cuisine.${c}`)} <span className="text-muted-foreground">({list(c).length})</span></h3>
            <div className="space-y-2">
              {list(c).map((d) => (
                <div key={d.id} className="rounded-xl bg-white/[0.06] ring-1 ring-white/10 p-2.5 flex items-center gap-2 group" data-testid={`dish-${d.id}`}>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{d.name}</div>
                    {d.allergens && <div className="text-[10px] font-bold text-pink-400 mt-0.5">{t("cuisine.allergens")} : {d.allergens}</div>}
                  </div>
                  {canEdit && <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                    <button data-testid={`dish-edit-${d.id}`} onClick={() => setForm({ ...d })} className="text-sky-400"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`dish-del-${d.id}`} onClick={() => remove(d)} className="text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>}
                </div>
              ))}
              {list(c).length === 0 && <p className="text-xs text-muted-foreground">{t("dish.empty")}</p>}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl glass p-4 text-xs text-muted-foreground">
        <b className="text-pink-400">{t("cuisine.allergensLegend")} :</b> {Array.from({ length: 14 }, (_, i) => `${i + 1} – ${t(`cuisine.allerg${i + 1}`)}`).join("   ")}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-md">
          {form && <>
            <DialogHeader><DialogTitle>{form.id ? t("dish.edit") : t("dish.add")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">{t("dish.name")}</Label><Input data-testid="dish-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">{t("dish.meal")}</Label>
                  <select data-testid="dish-form-meal" className="w-full h-10 rounded-md border px-2 bg-background" value={form.meal} onChange={(e) => setForm({ ...form, meal: e.target.value })}>{MEALS.map((m) => <option key={m} value={m}>{t(`cuisine.${m}`)}</option>)}</select></div>
                <div><Label className="text-xs">{t("dish.course")}</Label>
                  <select data-testid="dish-form-course" className="w-full h-10 rounded-md border px-2 bg-background" value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })}>{COURSES.map((c) => <option key={c} value={c}>{t(`cuisine.${c}`)}</option>)}</select></div>
              </div>
              <div><Label className="text-xs">{t("cuisine.allergens")} — {form.allergens || "—"}</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1">
                  {Array.from({ length: 14 }, (_, i) => i + 1).map((n) => {
                    const on = (form.allergens || "").split("/").includes(String(n));
                    return <button key={n} data-testid={`dish-allerg-${n}`} onClick={() => toggleAllerg(n)}
                      className={`px-2 py-1.5 rounded-lg text-[11px] font-semibold border transition text-left ${on ? "bg-pink-700 text-white border-pink-700" : "hover:bg-white/10"}`}>{n} – {t(`cuisine.allerg${n}`)}</button>;
                  })}
                </div></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setForm(null)}>{t("planning.cancel")}</Button>
              <Button className="bg-pink-700 hover:bg-pink-800 text-white" data-testid="dish-form-save" onClick={save}>{t("inv.save")}</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
